// Historia wygenerowanych listów (SR-035): lista wcześniejszych PDF CL / FUL z datą i autorem,
// zapis kopii po pobraniu i informacja o ostatnim wygenerowaniu.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  GeneratedDocument,
  GeneratedDocumentType,
  downloadGeneratedDocument,
  fetchGeneratedDocuments,
  storeGeneratedDocument,
} from "../../api/reportsApi";
import { downloadBlob } from "../../utils/exportUtils";

const queryKey = (reportId: string) => [reportId, "GeneratedDocuments"];

export const formatGeneratedAt = (iso: string) => {
  // Created z API jest w UTC bez oznaczenia strefy
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const useGeneratedDocuments = (reportId: string | undefined, type?: GeneratedDocumentType) => {
  const { data } = useQuery(queryKey(reportId ?? ""), () => fetchGeneratedDocuments(reportId!), { enabled: !!reportId });
  return (data ?? []).filter((d) => !type || d.documentType === type);
};

// Zapis kopii po pobraniu PDF. Błąd zapisu nie blokuje pobrania, tylko ostrzega.
export const useStoreGeneratedDocument = (reportId: string | undefined) => {
  const queryClient = useQueryClient();
  return async (type: GeneratedDocumentType, pdf: Blob, fileName: string) => {
    if (!reportId) return;
    try {
      await storeGeneratedDocument(reportId, type, pdf, fileName);
      queryClient.invalidateQueries(queryKey(reportId));
    } catch {
      toast.warn("PDF został pobrany, ale nie udało się zapisać kopii w historii dokumentów.");
    }
  };
};

export const GeneratedDocumentsList = ({ reportId, type }: { reportId: string | undefined; type: GeneratedDocumentType }) => {
  const docs = useGeneratedDocuments(reportId, type);

  const download = async (doc: GeneratedDocument) => {
    try {
      downloadBlob(await downloadGeneratedDocument(reportId!, doc.id), doc.fileName);
    } catch {
      toast.error("Nie udało się pobrać dokumentu.");
    }
  };

  return (
    <div className="rounded-lg border border-[var(--sc-border)] bg-[var(--sc-surface)]">
      <div className="px-3 py-2 border-b border-[var(--sc-border)] text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--sc-muted)]">
        Wcześniej wygenerowane ({docs.length})
      </div>
      {docs.length === 0 ? (
        <p className="px-3 py-2.5 text-xs text-[var(--sc-muted)]">
          Brak. Kopia zapisze się automatycznie po kliknięciu „Pobierz PDF”.
        </p>
      ) : (
        <ul className="max-h-48 overflow-y-auto divide-y divide-[var(--sc-border)]">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
              <div className="min-w-0">
                <div className="text-[var(--sc-text)]">{formatGeneratedAt(d.created)}</div>
                <div className="text-[var(--sc-muted)] truncate" title={d.fileName}>{d.createdBy}</div>
              </div>
              <button
                type="button"
                className="flex-shrink-0 text-[#3570C4] hover:underline"
                onClick={() => download(d)}
              >
                Pobierz
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Krótka informacja przy przyciskach listów w raporcie: kiedy i kto ostatnio wygenerował
export const LastGeneratedInfo = ({ reportId, type }: { reportId: string; type: GeneratedDocumentType }) => {
  const [last] = useGeneratedDocuments(reportId, type);
  if (!last) return null;
  return (
    <div className="text-[11px] text-[var(--sc-muted)] text-center -mt-1" title={last.createdBy}>
      wygenerowany {formatGeneratedAt(last.created)}
    </div>
  );
};
